import React from 'react';
import useCreateLeadNote from './useCreateLeadNote';
import usePatchLeadNote from './usePatchLeadNote';
import useDeleteLeadNote from './useDeleteLeadNote';
import { LeadNotePayload } from '../types';

interface UseStickyNoteParams {
    leadId: string;
    note: { id: number; body: string } | null;
    /** Receives the note the lead is left with — null after a delete. */
    onSaved: (latest: LeadNotePayload | null) => void;
}

// One sticky note per lead, edited from two places (the list's NoteModal and
// the detail panel). Both need the same create-vs-patch routing, the same
// "is this worth saving" test and the same pending flags, so that lives here
// rather than in each surface.
const useStickyNote = ({ leadId, note, onSaved }: UseStickyNoteParams) => {
    const [draft, setDraft] = React.useState(note?.body ?? '');

    const { mutate: createNote, isPending: isCreating } = useCreateLeadNote(onSaved);
    const { mutate: patchNote, isPending: isPatching } = usePatchLeadNote(onSaved);
    const { mutate: deleteNote, isPending: isDeleting } = useDeleteLeadNote((data) =>
        onSaved(data.next_latest_note)
    );

    const isSaving = isCreating || isPatching;
    const isPending = isSaving || isDeleting;
    const trimmed = draft.trim();
    const canSave = !isPending && !!trimmed && trimmed !== note?.body.trim();

    // `mutate` (not `mutateAsync`) keeps failures on the hooks' own onError,
    // which surfaces them through the notification context. The draft stays
    // put so the user can retry.
    const save = () => {
        if (!canSave) return;
        if (note) {
            patchNote({ leadId, noteId: note.id, payload: { body: trimmed } });
        } else {
            createNote({ payload: { lead_id: leadId, body: trimmed } });
        }
    };

    const remove = () => {
        if (!note || isPending) return;
        deleteNote({ leadId, noteId: note.id });
    };

    return { draft, setDraft, canSave, isSaving, isDeleting, isPending, save, remove };
};

export default useStickyNote;
